import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Download,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { loginSchema, type LoginInput } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

const FEATURES = [
  { icon: ShieldCheck, label: 'Role-based access, no surprises' },
  { icon: Zap, label: 'Built for speed — every page loads instantly' },
  { icon: Sparkles, label: 'Crafted UI, not bolted-on dashboards' },
] as const;

function LoadingSkeleton() {
  return (
    <div
      className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]"
      aria-busy="true"
      aria-label="Loading sign in page"
    >
      <div className="hidden bg-gradient-to-br from-teal-700 via-teal-600 to-teal-900 lg:block" />
      <div className="flex items-center justify-center bg-background p-6 md:p-10">
        <div className="w-full max-w-sm animate-pulse space-y-5">
          <div className="flex items-center gap-3 lg:hidden">
            <div className="h-11 w-11 rounded-xl bg-muted" />
            <div className="h-5 w-16 rounded bg-muted" />
          </div>
          <div className="space-y-2 pb-1">
            <div className="h-8 w-28 rounded-lg bg-muted" />
            <div className="h-4 w-52 rounded bg-muted" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-11 w-full rounded-lg bg-muted" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-20 rounded bg-muted" />
            <div className="h-11 w-full rounded-lg bg-muted" />
          </div>
          <div className="flex items-center gap-2.5">
            <div className="h-4 w-4 rounded bg-muted" />
            <div className="h-4 w-24 rounded bg-muted" />
          </div>
          <div className="h-11 w-full rounded-lg bg-muted" />
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('pms.login.rememberMe') === 'true',
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [forgotPasswordHint, setForgotPasswordHint] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: rememberMe ? (localStorage.getItem('pms.login.email') ?? '') : '',
      password: '',
    },
  });

  if (loading) return <LoadingSkeleton />;
  if (isAuthenticated) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: LoginInput) => {
    setFormError(null);
    setForgotPasswordHint(false);
    try {
      if (rememberMe) {
        localStorage.setItem('pms.login.email', values.email);
        localStorage.setItem('pms.login.rememberMe', 'true');
      } else {
        localStorage.removeItem('pms.login.email');
        localStorage.removeItem('pms.login.rememberMe');
      }
      await login(values.email, values.password);
      setLoginSuccess(true);
      setTimeout(() => {
        const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/dashboard';
        navigate(from, { replace: true });
      }, 600);
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorBody>;
      const msg = axiosErr.response?.data?.error?.message ?? 'Login failed. Please try again.';
      setFormError(msg);
    }
  };

  const handleForgotPassword = () => {
    setForgotPasswordHint(true);
    setFormError(null);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {/* LEFT — Hero panel (desktop only) */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-teal-700 via-teal-600 to-teal-900 p-12 lg:flex animate-in fade-in-0 duration-700">
        {/* Decorative blobs */}
        <div className="absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/40 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-amber-300/30 blur-3xl" />
        <div className="absolute right-1/4 top-1/3 h-64 w-64 rounded-full bg-teal-300/20 blur-3xl" />

        {/* Dot grid — radial-gradient cannot be expressed in Tailwind, inline style required */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative z-10 flex w-full flex-col justify-between text-white">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-xl font-bold backdrop-blur-sm ring-1 ring-white/30">
              R
            </div>
            <span className="text-base font-semibold tracking-tight">Raja Rani Couture Pvt Ltd</span>
          </div>

          {/* Headline + features */}
          <div className="max-w-md space-y-6">
            <h1 className="text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
              Manage your people,
              <br />
              <span className="text-amber-200">beautifully.</span>
            </h1>
            <p className="text-base text-teal-100/90 xl:text-lg">
              A modern People Management System built with care. Clean, fast, and designed for how
              teams actually work.
            </p>

            <div className="space-y-3 pt-2">
              {FEATURES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 text-sm text-teal-50">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
                    <Icon className="h-4 w-4" />
                  </div>
                  {label}
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-teal-200/70">© 2026 Raja Rani Couture Pvt Ltd · Foundation v0.1</p>
        </div>
      </div>

      {/* RIGHT — Form panel */}
      <div className="bg-mesh flex flex-col">
        {/* Mobile brand header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-teal-700 via-teal-600 to-teal-800 px-6 py-8 lg:hidden">
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-400/30 blur-2xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-amber-300/25 blur-2xl" />
          <div className="relative z-10 flex flex-col items-center gap-2 text-center text-white">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-xl font-bold backdrop-blur-sm ring-1 ring-white/30">
              R
            </div>
            <p className="text-base font-semibold tracking-tight">Raja Rani Couture Pvt Ltd</p>
            <p className="mt-0.5 text-xs text-teal-100/80">People Management System</p>
          </div>
        </div>

        {/* Form area */}
        <div className="flex flex-1 items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-sm animate-in fade-in-0 slide-in-from-bottom-3 duration-500 ease-out lg:slide-in-from-right-4">
            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Sign in
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Welcome back. Enter your credentials to continue.
              </p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              aria-label="Sign in form"
              className="space-y-5"
              noValidate
            >
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  aria-invalid={!!errors.email}
                  className="h-11 sm:h-11 bg-background transition-shadow focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-0"
                  {...register('email')}
                />
                {errors.email && (
                  <p
                    id="email-error"
                    role="alert"
                    className="flex items-center gap-1.5 text-xs text-destructive"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="rounded text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    aria-invalid={!!errors.password}
                    // `sm:` twins: Input's own `sm:h-10 sm:px-3` would otherwise win from
                    // `sm` up, shrinking the field and running text under the eye button.
                    className="h-11 sm:h-11 bg-background pr-12 sm:pr-12 transition-shadow focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-0"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute right-2.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p
                    id="password-error"
                    role="alert"
                    className="flex items-center gap-1.5 text-xs text-destructive"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2.5">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe((e.target as HTMLInputElement).checked)}
                  className="cursor-pointer"
                />
                <label
                  htmlFor="rememberMe"
                  className="cursor-pointer select-none text-sm text-muted-foreground"
                >
                  Remember me
                </label>
              </div>

              {/* Forgot password hint */}
              {forgotPasswordHint && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-start gap-3 animate-in fade-in-0 slide-in-from-top-1 duration-200 rounded-lg border border-primary/20 bg-accent px-4 py-3 text-sm"
                >
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Password reset required?</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Contact your administrator to reset your password.
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setForgotPasswordHint(false)}
                    className="-mt-0.5 ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Form error */}
              {formError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-start gap-3 animate-in fade-in-0 slide-in-from-top-1 duration-200 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="flex-1">{formError}</span>
                  <button
                    type="button"
                    aria-label="Dismiss error"
                    onClick={() => setFormError(null)}
                    className="-mt-0.5 ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Success banner */}
              {loginSuccess && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>Signed in successfully. Redirecting…</span>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="group h-11 w-full text-base font-medium shadow-sm shadow-primary/20 transition-shadow hover:shadow-primary/30"
                disabled={isSubmitting || loginSuccess}
              >
                {loginSuccess ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Signed in
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-xs text-muted-foreground">
              Trouble signing in?{' '}
              <button
                type="button"
                onClick={handleForgotPassword}
                className="rounded text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                Contact your administrator.
              </button>
            </p>

            {/* Download Android App */}
            <div className="mt-4 flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 px-4 py-3">
              <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="flex-1 text-xs text-muted-foreground">Use PMS on your Android phone</p>
              <a
                href="/downloads/pms-app.apk"
                download
                className="flex items-center gap-1 rounded text-xs font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <Download className="h-3 w-3" />
                Download APK
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
